// src/features/hotels/components/HotelCreate.tsx

import React, { useEffect, useState } from 'react';
import {
  Create,
  SimpleForm,
  TextInput,
  SelectInput,
  required,
  useTranslate,
  useNotify,
  useDataProvider,
  useRedirect
} from 'react-admin';
import { Box } from '@mui/material';
import { Business as HotelIcon } from '@mui/icons-material';

import { MyToolbar } from '../../../components/forms/MyToolbar';
import MyFormContainer from '../../../components/forms/MyFormContainer';
import { 
  CenteredFormLayout,
  MyFormSection 
} from '../../../components/forms/MyFormLayout';

import { useHotelValidation } from '../hooks/useHotelValidation';
import { prepareHotelForAPI, HOTEL_LANGUAGES } from '../types';

export const HotelCreate: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [groups, setGroups] = useState<Array<{id: string, name: string}>>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const redirect = useRedirect();


  const { 
    validateHotelName, 
    validateAddress, 
    validateInstallationCode,
    validateDefaultLanguage,
  } = useHotelValidation();

  const validateHotelNameForCreate = (value?: string) => {
    return validateHotelName(value, undefined);
  };

  const validateInstallationCodeForCreate = (value?: string) => {
    return validateInstallationCode(value, undefined);
  };

  const transform = (data: any) => {
  const preparedData = prepareHotelForAPI(data);
  return {
    ...preparedData,
    defaultLanguage: data.defaultLanguage || 'EN', 
    isActive: data.isActive ?? true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

  const onSuccess = () => {
    notify(
      translate('resources.hotels.notifications.created', 'Hotel creado con éxito'),
      { type: 'success' }
    );
    redirect('/hotels');
  };

  const onError = (error: any) => {
    console.error('Error creating hotel:', error);
    notify(
      error?.message || translate('resources.hotels.errors.create', 'Error al crear el hotel'),
      { type: 'error' }
    );
  };

 
  useEffect(() => {
    const loadGroups = async () => {
      setLoadingGroups(true);
      try {
        const response = await dataProvider.getList('groups', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'name', order: 'ASC' },
          filter: { isActive: true },
        });
        setGroups(response.data);
      } catch (error) {
        console.error('Error loading groups:', error);
        notify(translate('resources.hotels.errors.load_groups', 'Error al cargar grupos'), { type: 'error' });
      } finally {
        setLoadingGroups(false);
      }
    };

    loadGroups();
  }, [dataProvider, notify, translate]);

  return (
    <Create 
      title={translate('resources.hotels.titles.create', 'Crear Hotel')}
      transform={transform}
      mutationOptions={{ onSuccess, onError }}
    >
      <MyFormContainer>
        <SimpleForm
          toolbar={<MyToolbar showDelete={false} resource="hotels" />}
        >
          <CenteredFormLayout>
            <MyFormSection
              title={translate('resources.hotels.sections.basic_info', 'Información Básica')}
              subtitle={translate('resources.hotels.sections.basic_info_create_subtitle', 'Datos principales del nuevo hotel')}
              icon={<HotelIcon />}
              variant="outlined"
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 3 
              }}>
                {/* Campo de nombre del hotel */}
                <TextInput
                  size='small'
                  source="name"
                  label={translate('resources.hotels.fields.name', 'Nombre del Hotel')}
                  validate={[required(), validateHotelNameForCreate]}
                  fullWidth
                  helperText={translate(
                    'resources.hotels.help.name',
                    'Nombre único que identifica al hotel'
                  )}
                />

                {/* Fila con Grupo y Código de Instalación */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' } // Responsive: columna en móvil, fila en desktop
                }}>
                  <SelectInput
                    size='small'
                    source="groupId"
                    label={translate('resources.hotels.fields.group', 'Grupo')}
                    choices={groups}
                    optionText="name"
                    optionValue="id"
                    fullWidth
                    helperText={translate(
                      'resources.hotels.help.group',
                      'Grupo al que pertenece el hotel (opcional)'
                    )}
                    emptyText={translate('resources.hotels.no_groups', 'Seleccionar grupo...')}
                    isLoading={loadingGroups}
                  />

                  <TextInput
                    size='small'
                    source="installationCode"
                    label={translate('resources.hotels.fields.installationCode', 'Código de Instalación')}
                    validate={validateInstallationCodeForCreate}
                    fullWidth
                    helperText={translate(
                      'resources.hotels.help.installationCode',
                      'Formato: TAyy1234'
                    )}
                    placeholder="TAyy1234"
                  />
                </Box>

                {/* Campo de idioma por defecto */}

                <SelectInput
                  size='small'
                  source="defaultLanguage"
                  label={translate('resources.hotels.fields.defaultLanguage', 'Idioma por Defecto')}
                  choices={[...HOTEL_LANGUAGES]}
                  validate={validateDefaultLanguage}
                  defaultValue="EN"  // Cambiar de "es" a "EN" para consistencia con backend
                  fullWidth
                  helperText={translate(
                    'resources.hotels.help.defaultLanguage',
                    'Idioma por defecto para las interfaces del hotel'
                  )}
                />

                {/* Campo de dirección */}
                <TextInput
                  size='small'
                  source="address"
                  label={translate('resources.hotels.fields.address', 'Dirección')}
                  validate={validateAddress}
                  fullWidth
                  multiline
                  rows={1}
                  helperText={translate(
                    'resources.hotels.help.address',
                    'Dirección física del hotel'
                  )}
                />
              </Box>
            </MyFormSection>
          </CenteredFormLayout>
        </SimpleForm>
      </MyFormContainer>
    </Create>
  );
};

export default HotelCreate;